'use client';

import { Button } from '@finn/design-system/atoms/button';
import { Input } from '@finn/design-system/atoms/input';
import { FinnBlack } from '@finn/design-system/icons/FINN-black';
import { trackEvent, useIntl } from '@finn/ui-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter, useSearchParams } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { useCallback, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { isSameOriginReturnTo } from '../../../utils/returnTo';
import { PasswordInputWithToggle } from '../components/PasswordInputWithToggle';
import { emailSchema } from '../schemas';

const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Bitte geben Sie Ihr Passwort ein.'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const LoginPage = () => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<LoginFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const onSubmit = useCallback(
    async (values: LoginFormValues) => {
      setServerError(null);

      let result: Awaited<ReturnType<typeof signIn>>;
      try {
        result = await signIn('b2b-login', {
          redirect: false,
          email: values.email.trim(),
          password: values.password,
          b2b_auth_source_of_truth: 'next-auth',
        });
      } catch {
        trackEvent('b2b.login.failure', { reason: 'network_error' });
        setServerError(
          formatMessage(
            'auth.login.errors.generic',
            {
              defaultMessage:
                'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
            },
            { noTags: true }
          )
        );

        return;
      }

      // Normalize: next-auth can return ok:true with an error string
      if (result) {
        result.ok = result.ok && !result.error;
      }

      if (!result?.ok) {
        // Security: we show a single "invalid credentials" message for all
        // auth-related errors (CredentialsSignin, user_does_not_exist,
        // wrong_password) to prevent account enumeration. The generic error
        // is only shown for truly unexpected failures (network, server, etc.).
        const credentialErrors = new Set([
          'CredentialsSignin',
          'user_does_not_exist',
          'wrong_password',
        ]);

        const knownFailureReasons = new Set([
          'CredentialsSignin',
          'user_does_not_exist',
          'wrong_password',
          'b2b_user_deactivated',
          'b2b_provisioned_from_hubspot',
        ]);
        const failureReason =
          result?.error && knownFailureReasons.has(result.error)
            ? result.error
            : 'unknown';

        trackEvent('b2b.login.failure', { reason: failureReason });

        if (credentialErrors.has(result?.error ?? '')) {
          setServerError(
            formatMessage(
              'auth.login.errors.invalidCredentials',
              {
                defaultMessage: 'E-Mail-Adresse oder Passwort ist falsch.',
              },
              { noTags: true }
            )
          );
          // Unlike credential errors (collapsed above to prevent account enumeration),
          // this state is safe to surface distinctly: the account was admin-provisioned
          // (not self-registered), so revealing it does not leak whether an arbitrary
          // email belongs to a FINN customer. Accepted tradeoff for UX clarity.
        } else if (result?.error === 'b2b_provisioned_from_hubspot') {
          setServerError(
            formatMessage(
              'auth.login.errors.provisionedFromHubspot',
              {
                defaultMessage:
                  'Ihr Konto wurde soeben eingerichtet. Bitte prüfen Sie Ihre E-Mails, um Ihr Passwort festzulegen.',
              },
              { noTags: true }
            )
          );
          // Removed, or access ended. Auth answers this only after the right
          // password, so it tells nothing to someone who only knows the email.
        } else if (result?.error === 'b2b_user_deactivated') {
          setServerError(
            formatMessage(
              'auth.login.errors.accessEnded',
              {
                defaultMessage:
                  'Ihr Zugang zum Business Portal wurde beendet. Wenn Sie wieder Zugang brauchen, wenden Sie sich bitte an eine:n Administrator:in Ihres Unternehmens.',
              },
              { noTags: true }
            )
          );
        } else {
          setServerError(
            formatMessage(
              'auth.login.errors.generic',
              {
                defaultMessage:
                  'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
              },
              { noTags: true }
            )
          );
        }

        return;
      }

      trackEvent('b2b.login.success', {});

      const isSafeReturnTo = isSameOriginReturnTo(
        returnTo,
        window.location.origin
      );

      window.location.href = isSafeReturnTo ? returnTo! : '/';
    },
    [formatMessage, returnTo]
  );

  return (
    <div className="flex w-full max-w-md flex-col items-center gap-12">
      <FinnBlack className="h-8 w-24" />

      <div className="w-full text-center">
        <h1 className="global-t5-semibold mb-4">
          {formatMessage('auth.login.heading', {
            defaultMessage: 'Willkommen zurück',
          })}
        </h1>
        <p className="body-16-light mb-8">
          {formatMessage('auth.login.subtitle', {
            defaultMessage: 'Melden Sie sich in Ihrem Business Portal an.',
          })}
        </p>

        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex w-full flex-col gap-4"
            noValidate
          >
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="email"
                  autoComplete="email"
                  label={formatMessage(
                    'auth.login.emailLabel',
                    { defaultMessage: 'E-Mail-Adresse' },
                    { noTags: true }
                  )}
                  error={fieldState.error?.message}
                />
              )}
            />

            <PasswordInputWithToggle
              name="password"
              label={formatMessage(
                'auth.login.passwordLabel',
                { defaultMessage: 'Passwort' },
                { noTags: true }
              )}
            />

            {serverError && (
              <p className="text-red body-14-light m-0">{serverError}</p>
            )}
            <div className="mt-4 w-full">
              <Button type="submit" loading={isSubmitting} className="w-full">
                {formatMessage('auth.login.submitButton', {
                  defaultMessage: 'Anmelden',
                })}
              </Button>
              <Button
                variant="ghost"
                onClick={() =>
                  router.push(
                    `/forgot-password${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`
                  )
                }
                className="mt-2 w-full"
              >
                {formatMessage('auth.login.forgotPassword', {
                  defaultMessage: 'Passwort vergessen?',
                })}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default LoginPage;
